import { useStatistics } from '../hooks/useApi';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LogarithmicScale } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { useState } from 'react';
import { Link } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LogarithmicScale);

function AnalyticsDashboard() {
  const { data: stats, isLoading, error } = useStatistics();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 10;

  if (isLoading) {
    return (
      <div className="prose max-w-none">
        <h2 className="text-2xl font-semibold mb-4">Loading Statistics...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prose max-w-none">
        <h2 className="text-2xl font-semibold mb-4">Error Loading Statistics</h2>
        <p className="text-red-500">{error.message}</p>
      </div>
    );
  }

  const formatNumber = (num) => num?.toLocaleString() || '0';
  const formatScore = (num) => (num || 0).toFixed(2);

  const sourceData = {
    labels: ['Reddit', 'PoisCenter'],
    datasets: [
      {
        data: [
          stats?.messageStats?.redditCount || 0,
          stats?.messageStats?.poisCenterCount || 0,
        ],
        backgroundColor: [
          'orangered',
          '#385B7A',
        ],
        borderColor: [
          'white',
          'white',
        ],
        borderWidth: 2,
      },
    ],
  };

  const histogramData = {
    labels: stats?.authorStats?.relevanceHistogram?.bins.map((_, i) =>
      `${(i/10).toFixed(1)}-${((i+1)/10).toFixed(1)}`
    ) || [],
    datasets: [
      {
        label: 'Number of Discussions',
        data: stats?.authorStats?.relevanceHistogram?.bins || [],
        backgroundColor: (stats?.authorStats?.relevanceHistogram?.bins || []).map((_, i) =>
          i/10 >= 0.5 ? '#4B5563' : '#E5E7EB'
        ),
        borderColor: 'white',
        borderWidth: 1,
      },
    ],
  };

  const authorScoresData = {
    labels: stats?.authorStats?.topAuthors?.slice(0, 20).map(a => a.author) || [],
    datasets: [
      {
        label: 'Total Relevance Score',
        data: stats?.authorStats?.topAuthors?.slice(0, 20).map(a => a.totalScore) || [],
        backgroundColor: '#4B5563',
        borderColor: 'white',
        borderWidth: 1,
      },
    ],
  };

  const dateHistogramData = {
    labels: stats?.messageStats?.dateHistogram?.bins.map((_, i) => {
      const date = new Date(stats.messageStats.dateHistogram.startDate);
      date.setMonth(date.getMonth() + i);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
    }) || [],
    datasets: [
      {
        label: 'Number of Messages',
        data: stats?.messageStats?.dateHistogram?.bins || [],
        backgroundColor: '#4B5563',
        borderColor: '#374151',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const pieChartOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${formatNumber(value)} (${percentage}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false
  };

  const histogramOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return `Relevance Score: ${context[0].label}`;
          },
          label: function(context) {
            return `Discussions: ${formatNumber(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Relevance Score Range',
          padding: 10,
          font: {
            size: window.innerWidth < 640 ? 10 : 12
          }
        },
        grid: {
          display: false
        },
        ticks: {
          maxRotation: window.innerWidth < 640 ? 45 : 0,
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          }
        }
      },
      y: {
        type: 'logarithmic',
        title: {
          display: true,
          text: 'Number of Discussions',
          padding: 10,
          font: {
            size: window.innerWidth < 640 ? 10 : 12
          }
        },
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          }
        }
      }
    }
  };

  const authorScoresOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `Total Score: ${formatScore(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Author',
          padding: 10,
          font: {
            size: window.innerWidth < 640 ? 10 : 12
          }
        },
        grid: {
          display: false
        },
        ticks: {
          maxRotation: window.innerWidth < 640 ? 65 : 45,
          minRotation: window.innerWidth < 640 ? 65 : 45,
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          }
        }
      },
      y: {
        type: 'logarithmic',
        title: {
          display: true,
          text: 'Total Relevance Score (log scale)',
          padding: 10
        },
        beginAtZero: true,
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          }
        }
      }
    }
  };

  const dateHistogramOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `Messages: ${formatNumber(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          padding: 10,
          font: {
            size: window.innerWidth < 640 ? 10 : 12
          }
        },
        grid: {
          display: false
        },
        ticks: {
          maxRotation: window.innerWidth < 640 ? 65 : 45,
          minRotation: window.innerWidth < 640 ? 65 : 45,
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          },
          autoSkip: true,
          maxTicksLimit: window.innerWidth < 640 ? 6 : 12
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Messages',
          padding: 10,
          font: {
            size: window.innerWidth < 640 ? 10 : 12
          }
        },
        beginAtZero: true,
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: window.innerWidth < 640 ? 8 : 10
          },
          callback: function(value) {
            return formatNumber(value);
          }
        }
      }
    }
  };

  const filteredAuthors = stats?.authorStats?.topAuthors?.filter(author =>
    author.author.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalAuthors = filteredAuthors.length;
  const totalPages = Math.ceil(totalAuthors / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentAuthors = filteredAuthors.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Add this function to get the original rank of an author
  const getOriginalRank = (author) => {
    return (stats?.authorStats?.topAuthors?.findIndex(a => a.author === author.author) || 0) + 1;
  };

  return (
    <div className="prose max-w-none px-3 sm:px-6 py-3 sm:py-6">
      <h2 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-4" id="overview">Data Analytics Dashboard</h2>

      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
        poisdata.org aggregates and analyzes medical discussions from Reddit and PoisCenter
        to generate structured case studies using clinical NLP. The system processes both
        patient-reported experiences and clinical data to identify treatment patterns and
        biomedical correlations in POIS research.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Messages Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100" id="messages">
          <div className="p-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3">Messages by Source</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">
              Total Messages: {formatNumber(stats?.messageStats?.totalCount)}
            </p>
            <div style={{ height: '200px' }}>
              <Pie data={sourceData} options={{
                ...pieChartOptions,
                plugins: {
                  ...pieChartOptions.plugins,
                  legend: {
                    position: 'bottom',
                    labels: {
                      boxWidth: window.innerWidth < 640 ? 10 : 12,
                      padding: window.innerWidth < 640 ? 8 : 12,
                      font: {
                        size: window.innerWidth < 640 ? 10 : 12
                      }
                    }
                  }
                }
              }} />
            </div>
          </div>
        </div>

        {/* Author Discussions Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100" id="discussions">
          <div className="p-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3">Author Discussions</h3>
            <div className="space-y-1">
              <p className="text-xs sm:text-sm text-gray-600">
                Total Discussions: {formatNumber(stats?.authorStats?.totalDiscussions)}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">
                Unique Authors: {formatNumber(stats?.authorStats?.uniqueAuthors)}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-2">
                Average Posts per Author: {formatNumber(Math.round(stats?.authorStats?.totalDiscussions / (stats?.authorStats?.uniqueAuthors || 1)))}
              </p>
            </div>
            <div style={{ height: '200px' }}>
              <Bar data={histogramData} options={histogramOptions} />
            </div>
          </div>
        </div>

        {/* Case Studies Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100" id="case-studies">
          <div className="p-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3">Case Studies</h3>
            <div className="space-y-1">
              <p className="text-xs sm:text-sm text-gray-600">
                Total Case Studies: {formatNumber(stats?.caseStudyStats?.totalCount)}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-2">
                Average Relevance Score: {formatScore(stats?.caseStudyStats?.avgRelevanceScore)}
              </p>
            </div>
            <div style={{ height: '200px' }}>
              <Bar data={authorScoresData} options={authorScoresOptions} />
            </div>
          </div>
        </div>

        {/* Date Range Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100" id="date-range">
          <div className="p-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3">Message Timeline</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">
              Data Range: {stats?.messageStats?.dateRange?.earliest ? new Date(stats.messageStats.dateRange.earliest).toLocaleDateString() : 'N/A'}
              {' '} to {' '}
              {stats?.messageStats?.dateRange?.latest ? new Date(stats.messageStats.dateRange.latest).toLocaleDateString() : 'N/A'}
            </p>
            <div style={{ height: '200px' }}>
              <Bar data={dateHistogramData} options={dateHistogramOptions} />
            </div>
          </div>
        </div>

        {/* Top Authors */}
        <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-100" id="top-authors">
          <div className="p-4">
            <div className="flex flex-col space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                <h3 className="text-base sm:text-xl font-semibold">Top Authors by Relevance Score</h3>
                <div className="text-sm text-gray-600">
                  Showing {totalAuthors > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, totalAuthors)} of {totalAuthors}
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search authors..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
              </div>
            </div>
            <div className="overflow-x-auto -mx-3 sm:mx-0 mt-3 sm:mt-4">
              <div className="min-w-full inline-block align-middle">
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed sm:table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-500">Rank</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-500">Author</th>
                        <th className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm font-medium text-gray-500">Total Score</th>
                        <th className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm font-medium text-gray-500">Average Score</th>
                        <th className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm font-medium text-gray-500">Discussions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentAuthors.map((author, index) => (
                        <tr key={author.author} className="hover:bg-gray-50">
                          <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm">{getOriginalRank(author)}</td>
                          <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">
                            <Link
                              to={`/case-study/${encodeURIComponent(author.author)}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {author.author}
                            </Link>
                          </td>
                          <td className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm">{formatScore(author.totalScore)}</td>
                          <td className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm">{formatScore(author.averageScore)}</td>
                          <td className="px-3 sm:px-4 py-2 text-right text-xs sm:text-sm">{formatNumber(author.discussionCount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 flex flex-wrap justify-center items-center gap-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed hidden sm:block"
                    aria-label="First page"
                  >
                    «
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Previous page"
                  >
                    <span className="sm:hidden">Prev</span>
                    <span className="hidden sm:block">‹</span>
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1) ||
                      (page === 2 && currentPage <= 4) ||
                      (page === totalPages - 1 && currentPage >= totalPages - 3)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`min-w-[32px] h-8 px-2 rounded border ${
                            currentPage === page
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'hover:bg-gray-50 text-gray-700'
                          } hidden sm:block`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      (page === currentPage - 2 && currentPage > 4) ||
                      (page === currentPage + 2 && currentPage < totalPages - 3)
                    ) {
                      return <span key={page} className="hidden sm:block">...</span>;
                    }
                    return null;
                  })}
                  {/* Mobile page indicator */}
                  <span className="sm:hidden text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Next page"
                  >
                    <span className="sm:hidden">Next</span>
                    <span className="hidden sm:block">›</span>
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed hidden sm:block"
                    aria-label="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;